import "./GlobalHeader.scss";

import * as PropTypes from "prop-types";
import * as React from "react";
import { Navbar } from "react-bootstrap";
import * as _ from "lodash";
import classNames from "classnames";

import CountryUtil from "shared/src/uidispatcherapi/CountryUtil";
import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";
import { isTier12Customer } from "shared/src/stores/SessionStore";
import { connect } from "shared/src/stores/storeConnect";
import StoreSelectionInfo from "./../StoreSelectionInfo";
import MetroBackButton from "./../MetroBackButton";
import MobileHeader from "../mobileheader/MobileHeader";
import RouterUtil from "../../../utils/RouterUtil";
import SearchInput from "./../SearchInput";
import BrandbarAccountInformation from "./accountFlyout/BrandbarAccountInformation";
import StoreSelectionPopover from "./store/StoreSelectionPopover";
import NavigationMenuBar from "./NavigationMenuBar";
import CustomerStore from "../../../stores/CustomerStore";
import { isAnonCartEnabled, isMetroX, useExternalNavigationWebComponent } from "../../../searchdiscover/toggles";
import { isConfigured as isCmsNavigationMenuConfigured } from "./CmsNavigationMenu";
import { MetroXStoreSelector } from "./store/MetroXStoreSelector";
import * as GlobalHeaderUtil from "../../../utils/GlobalHeaderUtil";
import { withReactAdapter } from "shared/src/module-federation/withReactAdapter";
import { getComponentByName } from "shared/src/components/LazyWrapper";
const MiniShoppingCart = getComponentByName("oc_ui/MiniCartSelector");

type Props = {
    reduced?: boolean;
    hideStoreSelectionInfo?: boolean;
    children?: React.ReactNode;
    location: any;
    isCurrentCustomerFsdCustomer?: boolean;
};

class GlobalHeader extends React.Component<Props> {
    static contextTypes = {
        router: PropTypes.object.isRequired
    };

    navigationMenuRef: React.RefObject<any> = React.createRef();

    lastScrollY: number = 0;
    mobileBreakpoint: number = 768;
    lastPathname: string = "";

    state = {
        shoppingCartNotificationContainer: null as any,
        popoverContainer: null as any,
        visible: true,
        isMobile: typeof window !== "undefined" ? window.innerWidth < 768 : false
    };

    handleScroll = () => {
        if (!this.state.isMobile) return;

        const currentScrollY = window.scrollY;
        const deltaY = currentScrollY - this.lastScrollY;

        // Update lastScrollY before any visibility changes
        this.lastScrollY = currentScrollY;

        // Always show header at top of page or on pathname change
        if (currentScrollY <= 0 || this.props.location?.pathname !== this.lastPathname) {
            this.setState({ visible: true });
            this.lastPathname = this.props.location?.pathname;
            return;
        }

        // Update visibility on any scroll (not just when exceeding threshold)
        if (deltaY !== 0) {
            this.setState({ visible: deltaY < 0 });
        }
    };

    handleResize = () => {
        const isMobile = window.innerWidth < this.mobileBreakpoint;
        if (isMobile !== this.state.isMobile) {
            this.setState({
                isMobile: isMobile,
                visible: true
            });
            this.lastScrollY = isMobile ? window.scrollY : 0;
        }
    };

    componentDidMount() {
        this.lastScrollY = window.scrollY;
        this.lastPathname = this.props.location?.pathname;
        this.handleResize(); // Sets initial isMobile and resets visible state if needed

        window.addEventListener("scroll", this.handleScroll, { passive: true });
        window.addEventListener("resize", this.handleResize, { passive: true });
    }

    componentDidUpdate(prevProps: Readonly<Props>) {
        if (this.props.location?.pathname !== prevProps.location?.pathname) {
            this.lastScrollY = 0;
        }
    }

    componentWillUnmount() {
        window.removeEventListener("scroll", this.handleScroll);
        window.removeEventListener("resize", this.handleResize);
    }

    toggleMenu = (state: boolean) => {
        if (this.navigationMenuRef.current) {
            this.navigationMenuRef.current.toggleMobileMenu(state);
        }
    };

    onQuery = (query: string, suggestion: boolean, facets: string | undefined | null) => {
        let searchParams = { q: query } as { [k: string]: any };
        if (facets) {
            searchParams = { facets: facets };
        }
        if (suggestion) {
            _.assign(searchParams, { s: suggestion });
        }
        RouterUtil.redirectToSearch(searchParams, this.context.router);
    };

    isShoppingCartVisible() {
        if (CountryUtil.getOptionalConfig().hideMiniShoppingCartForNonFsd && !this.props.isCurrentCustomerFsdCustomer) {
            return false;
        }
        return isAnonCartEnabled() || (SessionStore.loggedIn() && !isTier12Customer());
    }

    renderMetroXDesktopStoreSelector() {
        return (
            <div className={"metrox-store-selector-container-desktop"}>
                <MetroXStoreSelector />
            </div>
        );
    }

    renderClassicStoreSelectionPopover() {
        return <StoreSelectionPopover popoverContainer={this.state.popoverContainer} />;
    }

    renderStoreSelector() {
        if (GlobalHeaderUtil.shouldShowMetroXStoreSelector()) {
            return !this.props.reduced && this.renderMetroXDesktopStoreSelector();
        }
        return !this.props.reduced && this.renderClassicStoreSelectionPopover();
    }

    getVisibilityOfMobileAndDesktopNavbars(shouldShowExternalNavigationComponent: boolean) {
        // Checking for shouldShowMetroXStoreSelector() is meant to make switching between desktop and mobile view
        // consistent with other parts of the metro website, where explore-ui used special breakpoints in the past
        if (shouldShowExternalNavigationComponent || GlobalHeaderUtil.shouldShowMetroXStoreSelector()) {
            return ["visible-sm visible-xs", "hidden-sm hidden-xs"];
        }
        return ["visible-xs", "hidden-xs"];
    }

    render() {
        const miniShoppingCart = this.isShoppingCartVisible() ? (
            <MiniShoppingCart
                navItem
                popoverContainer={this.state.shoppingCartNotificationContainer}
                className="navigationbar-cart"
            />
        ) : null;
        const shouldShowExternalNavigationComponent =
            useExternalNavigationWebComponent() && isCmsNavigationMenuConfigured();
        const [mobileClass, desktopClass] = this.getVisibilityOfMobileAndDesktopNavbars(
            shouldShowExternalNavigationComponent
        );
        const storeSelector = this.renderStoreSelector();
        const desktopSecondNav = (
            <Navbar
                default
                fluid
                staticTop
                className={"header-brandbar " + desktopClass + (isMetroX() ? " metrox fixed-width-navbar" : "")}
                ref={(ref) => {
                    if (!this.state.popoverContainer) this.setState({ popoverContainer: ref });
                }}>
                {!isMetroX() && (
                    <Navbar.Header>
                        <MetroBackButton country={SessionStore.getCountry()} to={{ pathname: "/" }} />
                    </Navbar.Header>
                )}
                {!isMetroX() && <BrandbarAccountInformation />}
                {isMetroX() && miniShoppingCart}
                {storeSelector}
                {!this.props.reduced && (
                    <div className="brandbar-search">
                        <SearchInput
                            onQuery={this.onQuery}
                            initialValue={_.get(this.props, ["location", "query", "q"], "")}
                            placeholderId="SEARCH_EVERYWHERE_PLACEHOLDER"
                            partOfGlobalHeader
                            enableSuggestions
                        />
                    </div>
                )}
            </Navbar>
        );
        const desktopThirdNav =
            !this.props.reduced &&
            (shouldShowExternalNavigationComponent ? (
                <NavigationMenuBar
                    cmsMenuRef={this.navigationMenuRef}
                    miniShoppingCart={miniShoppingCart}
                    isCurrentCustomerFsdCustomer={this.props.isCurrentCustomerFsdCustomer}
                    useExternalNavigation
                />
            ) : (
                <Navbar
                    fluid
                    inverse
                    staticTop
                    ref={(ref) => {
                        if (!this.state.shoppingCartNotificationContainer)
                            this.setState({ shoppingCartNotificationContainer: ref });
                    }}
                    className={
                        "navigationbar header-navigationbar clearfix " +
                        desktopClass +
                        (isMetroX() ? " fixed-width-navbar" : "")
                    }>
                    <NavigationMenuBar miniShoppingCart={miniShoppingCart} />
                </Navbar>
            ));
        const mobileHeader = (
            <div className={mobileClass}>
                <MobileHeader
                    isUsingExternalNavigationComponent={shouldShowExternalNavigationComponent}
                    onToggleMenu={this.toggleMenu}
                    onQuery={this.onQuery}
                    placeholderId="SEARCH_EVERYWHERE_PLACEHOLDER"
                    partOfGlobalHeader
                    enableSuggestions
                    reduced={this.props.reduced}
                    shoppingCartVisible={this.isShoppingCartVisible()}
                    country={SessionStore.getCountry()}
                    __bypassReactAdapter>
                    {this.props.children || null}
                </MobileHeader>
            </div>
        );

        const { isMobile, visible } = this.state;
        const usePlaceholderHeight = isMobile && !visible && this.lastScrollY > 0;

        return (
            <React.Fragment>
                {!this.props.hideStoreSelectionInfo && <StoreSelectionInfo />}
                <div
                    className={classNames("mfcss_header", {
                        "header-height-employee": usePlaceholderHeight && SessionStore.isEmployee(),
                        "header-height-customer": usePlaceholderHeight && !SessionStore.isEmployee()
                    })}>
                    {desktopSecondNav}
                    {mobileHeader}
                    {desktopThirdNav}
                    <div className={desktopClass}>{this.props.children}</div>
                    <div id="header-addon-portal-target" />
                </div>
            </React.Fragment>
        );
    }
}

export default withReactAdapter(
    connect(
        {
            stores: [
                [
                    CustomerStore,
                    (store: typeof CustomerStore) => ({
                        isCurrentCustomerFsdCustomer: store.isCurrentCustomerFsdCustomer()
                    })
                ]
            ]
        },
        GlobalHeader,
        true
    )
);
